import axios from 'axios'
import toast from 'react-hot-toast'
import { bffBaseURL, normalizeError } from './apiClient'

// ============================================================================
// Knowledge Management API — Admin Only
// ============================================================================

export async function getKnowledgeOverview() {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/knowledge/overview`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast.error('Failed to load knowledge overview')
    }
    throw error
  }
}

export async function getKnowledgeMetadata() {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/knowledge/metadata`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to load validation configuration')
      throw new Error(message)
    }
    throw error
  }
}

export async function getSystemStatus() {
  try {
    // Task 4.6.estado.1: Use /health endpoint consistently (no fallback pattern)
    // BFF returns: { operations_portal: { details: { dependencies: { ingestion_service, redis } } } }
    const response = await axios.get(`${bffBaseURL}/health`)
    const data = response.data || {}
    // BUG-024a Fix: Read from correct path in BFF response
    const dependencies = data.operations_portal?.details?.dependencies || {}

    const timestamp = data.timestamp || new Date().toISOString()

    // BUG-024 Fix: Extract real metrics from backend response
    // Ingestion service details come from dependencies.ingestion_service.details
    const ingestionData = dependencies.ingestion_service || {}
    const ingestionDetails = ingestionData.details || {}
    const ingestionService = {
      status: ingestionData.status || 'unknown',
      available: ingestionData.status === 'healthy' || ingestionData.status === 'disabled',
      statusCode: ingestionData.status_code ?? null,
      error: ingestionData.error || null,
      lastCheck: timestamp,
      url: ingestionData.url || ingestionData.target || ingestionDetails.url || null,
      // BUG-024: Extract version and queue from ingestion service health response
      latency: ingestionData.latency_ms ?? null,
      version: ingestionDetails.version ?? null,
      queueSize: ingestionDetails.queue?.pending ?? null,
      queueInProgress: ingestionDetails.queue?.in_progress ?? null,
    }

    // Map dependencies.redis → sessionStore (Redis is the session store)
    // BUG-024: Extract memory and version from Redis INFO
    const redisData = dependencies.redis || {}
    const sessionStore = {
      status: redisData.status || 'unknown',
      available: redisData.status === 'healthy',
      error: redisData.error || null,
      lastCheck: timestamp,
      // BUG-024: Extract real Redis metrics
      latency: redisData.latency_ms ?? null,
      memoryUsage: redisData.memory_used ?? null,
      redisVersion: redisData.redis_version ?? null,
    }

    return {
      status: data.status || 'unknown',
      timestamp,
      featureFlags: data.feature_flags || {},
      ingestionService,
      sessionStore,
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to load system status')
      throw new Error(message)
    }
    throw error
  }
}

export async function getDocuments(params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/knowledge/documents${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast.error('Failed to load documents')
    }
    throw error
  }
}

export async function uploadDocuments(files: File[], options: Record<string, any> = {}) {
  try {
    // Phase 76 M5: Pre-flight validation removed. BFF gate("knowledge") enforces access.
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })

    if (options.skipDuplicates !== undefined) {
      formData.append('skip_duplicates', options.skipDuplicates)
    }
    if (options.enableEnrichment !== undefined) {
      formData.append('enable_enrichment', options.enableEnrichment)
    }

    const response = await axios.post(
      `${bffBaseURL}/api/knowledge/documents/batch`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Operation': 'document-upload'
        },
      }
    )

    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Batch upload failed')
      throw new Error(message)
    }
    throw error
  }
}

export async function batchUploadDocuments(formData: FormData, groupId: string | null = null) {
  try {
    let url = `${bffBaseURL}/api/knowledge/documents/batch`
    if (groupId) {
      url += `?group_id=${encodeURIComponent(groupId)}`
    }

    const response = await axios.post(
      url,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Operation': 'document-upload'
        },
      }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Batch upload failed')
      throw new Error(message)
    }
    throw error
  }
}

// Phase 61: Knowledge Categories API

export async function getCategories(includeSystem: boolean = true) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/categories?include_system=${includeSystem}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'No se pudieron obtener las categorías')
      throw new Error(message)
    }
    throw error
  }
}

export async function getDocumentCategories(documentId: number | string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/knowledge/documents/${documentId}/categories`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'No se pudieron obtener las categorías del documento')
      throw new Error(message)
    }
    throw error
  }
}

export async function updateDocumentCategories(documentId: number | string, groupIds: string[]) {
  try {
    const response = await axios.patch(
      `${bffBaseURL}/api/knowledge/documents/${documentId}/categories`,
      { group_ids: groupIds }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'No se pudieron actualizar las categorías')
      throw new Error(message)
    }
    throw error
  }
}

export async function getUserBatchJobs() {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/knowledge/batch-jobs`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'No se pudieron obtener los trabajos en cola')
      throw new Error(message)
    }
    throw error
  }
}

export async function getBatchJobStatus(jobId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/knowledge/batch-jobs/${jobId}`
    )
    const data = response.data || {}
    const tracker = data.tracker || {}
    const queueRecord = data.queue_record || {}

    const trackerProgress = tracker.progress || {}
    const queueTotals = {
      completed_files: queueRecord.completed_files ?? 0,
      failed_files: queueRecord.failed_files ?? 0,
      total_files: queueRecord.total_files ?? (Array.isArray(queueRecord.files) ? queueRecord.files.length : 0),
      current_file: queueRecord.current_file || null,
      errors: Array.isArray(queueRecord.error_summary) ? queueRecord.error_summary : []
    }

    const normalizedProgress = {
      completed_files: trackerProgress.completed_files ?? queueTotals.completed_files,
      failed_files: trackerProgress.failed_files ?? queueTotals.failed_files,
      total_files: trackerProgress.total_files ?? queueTotals.total_files,
      current_file: trackerProgress.current_file ?? queueTotals.current_file,
      errors: Array.isArray(trackerProgress.errors)
        ? trackerProgress.errors
        : queueTotals.errors
    }

    return {
      jobId: data.job_id || queueRecord.id || jobId,
      status: tracker.status || queueRecord.status || data.status || 'queued',
      progress: normalizedProgress,
      tracker,
      queueRecord,
      metadata: data.metadata || queueRecord.metadata || {},
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'No se pudo obtener el estado del trabajo')
      throw new Error(message)
    }
    throw error
  }
}

export async function uploadDocument(file: File) {
  return uploadDocuments([file])
}

export async function searchDocuments(request: any) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/knowledge/search`,
      request
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast.error('Search failed')
    }
    throw error
  }
}

export async function deleteDocument(documentId: number | string) {
  try {
    // Phase 76 M5: Pre-flight validation removed. BFF gate("knowledge") enforces access.
    const response = await axios.delete(
      `${bffBaseURL}/api/knowledge/documents/${documentId}`,
      {
        headers: {
          'X-Admin-Operation': 'document-delete'
        }
      }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Delete failed')
      throw new Error(message)
    }
    throw error
  }
}

export async function reprocessDocument(documentId: number | string) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/knowledge/documents/${documentId}/reprocess`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Reprocess failed')
      toast.error(`Reprocess failed: ${message}`)
      throw new Error(message)
    }
    throw error
  }
}
